
import React from "react";
import * as Hooks from "../../../../hooks";
import { i18nManager } from "../../../../i18n";
import * as ReactRouterDom from "react-router-dom";
import { Capability, Schemes, Utils } from "../../../../utils";
import MediaInput from "../../../shared/MediaInput";
import StatefulLink from "../../../shared/StatefulLink";
import { ConfirmDialog, Siren } from "../../../shared/ConfirmDialog";
import LoadingGif from "../../../../assets/images/loading_circle.gif";
import { BaseService, ConsusService, IMember, IProfileWebsite, IUploadedFile, MemberService, SanitizedResponse } from "../../../../services";
import { BoolHelper, ButtonManageRef, Classname, NoseurObject, ObjectHelper, Popover, PopoverManageRef, TabPane, TabPanel } from "@rivtn/noseur";
import UpSellPlan from "../../../shared/UpSellPlan";

export interface MediaProps {
    profile: IMember;
}

function Media(props: MediaProps) {

    const { profile } = props;
    const [themeMode, _] = Hooks.useThemeMode();
    const consusService = ConsusService.getInstance();
    const memberService = MemberService.getInstance();
    const addMediaElement = React.useRef<HTMLSpanElement>(null);
    const labels = i18nManager.Labels.account.profile_website.media;
    const tabs = labels.sections.map((s) => s.key);
    const uploadPopOverManageRef = React.useRef<PopoverManageRef>(null);
    const [profileWebsite, setProfileWebsite] = React.useState<IProfileWebsite>();
    const [searchParams, setSearchParams] = ReactRouterDom.useSearchParams(window.location.search);
    const mediaSectionLimit: NoseurObject<number> = {
        LOGO: 5,
        BANNER: 3,
        PASSPORT: 4,
        HALF_BODY: 3,
        SELF_ANIMATION: 3,
    }; // TODO fetch from their plan from backend
    const mediaSectionMimeType: NoseurObject<string> = {
        SELF_ANIMATION: "video/*",
    };
    const mediaSectionStyle: NoseurObject<React.CSSProperties | undefined> = {
        LOGO: {
            width: 200,
            height: 200,
            borderRadius: 200,
        },
        BANNER: {
            height: 300,
            width: "100%",
            borderRadius: 10,
        },
        PASSPORT: {
            width: 200,
            height: 200,
            borderRadius: 10,
        },
        HALF_BODY: {
            width: 300,
            height: 400,
            borderRadius: 10,
        },
        SELF_ANIMATION: {
            width: 500,
            height: 300,
            borderRadius: 10,
        },
    };

    React.useEffect(() => {
        memberService.getProfileWebsite().then(({ sanitized }: SanitizedResponse<IProfileWebsite>) => {
            setProfileWebsite(sanitized);
        }).catch(BaseService.reportError);
    }, []);

    return (<div className="account-setting">
        <div className="header">
            <span className="title">{i18nManager.Labels.account.profile_website.title} - {labels.title}</span>
        </div>
        {!profileWebsite
            ? (<div style={{ margin: "90px 110px" }}>
                <img alt={"loading"} src={LoadingGif} style={{ width: 80, margin: "auto" }} />
            </div>)
            : (<TabPane renderActiveTabOnly noUnderline idleScheme={Schemes.IDLE} scheme={Schemes.OMNEHOST} activeIndex={tabs.indexOf(searchParams.get("tab") ?? "LOGO")} style={{ margin: "10px 0px" }} onTabActive={(i) => {
                Utils.updateSearchParams("tab", tabs[i], setSearchParams);
            }}>
                {labels.sections.map((section) => {
                    const sectionStyle = mediaSectionStyle[section.key];
                    const sectionLimit = mediaSectionLimit[section.key];
                    const sectionMedias = ((profileWebsite?.media as any)?.[section.key] ?? []) as string[];

                    return (<TabPanel key={section.key} header={section.title}>
                        <p>{section.desc}</p>
                        <p>{ObjectHelper.expandStringTemplate(section.desc2, {
                            limit: sectionLimit,
                        }, { chop: "$" })}</p>
                        <div className="media-uploads">
                            {sectionMedias.map((url: string, index: number) => {
                                const isActive = Math.min((profileWebsite.extra_data?.media as any)?.[section.key]?.active ?? 0, sectionMedias.length - 1) === index;

                                return (<div key={url} className={Classname.build("media-upload", { "active": isActive })} style={sectionStyle} onClick={() => {
                                    setDefaultMedia(section.key, index);
                                }}>
                                    {isActive && (<i className="fa fa-check" />)}
                                    {section.key === "SELF_ANIMATION"
                                        ? (<video controls>
                                            <source src={url} />
                                        </video>)
                                        : (<img src={url} alt={section.key} />)}
                                    {!(BoolHelper.equalsAny(section.key, ["LOGO", "PASSPORT", "BANNER"]) && index === 0) && (<StatefulLink className="delete" label={<i className="fa fa-trash" />} onClick={(done, e) => {
                                        e.stopPropagation();
                                        deleteMedia(section.key, url, isActive, done);
                                    }} />)}
                                </div>);
                            })}

                            <UpSellPlan successOnNoop type="shallow_icon" capability={`maxProfileWebsite${Utils.toSaneSentenceFormat(section.key).replaceAll(' ', "")}Upload`} currentValue={sectionMedias.length ?? 0} childrenRelayProps={{ style: { ...sectionStyle, opacity: 0.4 } }} >
                                {sectionMedias.length < sectionLimit && (<div className="media-upload uploader" style={sectionStyle} onClick={(e: any) => uploadPopOverManageRef.current?.toggle(e, addMediaElement.current ?? undefined)}>
                                    <i ref={addMediaElement} className="fa fa-plus" />
                                </div>)}
                            </UpSellPlan>
                        </div>
                    </TabPanel>);
                })}
            </TabPane>)}

        <Popover dismissOnClick manageRef={uploadPopOverManageRef} className={Classname.build("noseur-popover-b np-media", themeMode)} style={{ minWidth: 90, marginTop: 55 }}>
            <UpSellPlan successOnNoop type="shallow" capability={Capability.GENERATE_PROFILE_WEBSITE_MEDIA_WITH_AI} childrenRelayProps={{ readOnly: true, scheme: Schemes.GOLDEN, style: { padding: "10px 35px", color: "#FFD700", fontWeight: "bold" } }} >
                <span onClick={() => uploadMedia("GENERATE_USING_AI", labels.sections.find((s) => s.key === (searchParams.get("tab") ?? "LOGO"))!)}>{labels.upload.popover.generate_with_ai}</span>
            </UpSellPlan>
            <span onClick={() => uploadMedia("UPLOAD_FROM_DEVICE", labels.sections.find((s) => s.key === (searchParams.get("tab") ?? "LOGO"))!)}>{labels.upload.popover.upload_from_device}</span>
        </Popover>
    </div>);

    function updateProfileWebsite(updatedProfileWebsite: any) {
        memberService.updateProfileWebsite(updatedProfileWebsite!).then(({ sanitized }: SanitizedResponse<IProfileWebsite>) => {
            setProfileWebsite(sanitized);
            Siren.alert(i18nManager.Labels.account.profile_website.update_successful);
        }).catch(BaseService.reportError);
    }

    function uploadMedia(type: "GENERATE_USING_AI" | "UPLOAD_FROM_DEVICE", section: { key: string; desc: string; desc2: string; title: string; }) {
        if (!profileWebsite) return;
        const dialogErrorRef = { current: null } as any;
        const mediaInputManageRef = { current: undefined as any };
        const sectionMimeType = mediaSectionMimeType[section.key];
        const mediaSectionLength = ((profileWebsite?.media as any)?.[section.key] ?? []).length;
        const sectionStyle = { ...mediaSectionStyle[section.key], ...(section.key === "BANNER" ? { width: 500 } : {}) };

        const dialog = ConfirmDialog({
            closable: true,
            style: { minWidth: 400, maxWidth: "80%" },
            confirmLabel: i18nManager.Labels.common.upload,
            title: ObjectHelper.expandStringTemplate(labels.upload.dialog.title, {
                section: section.title,
            }, { chop: "$" }),
            content: (<div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "30px 50px" }}>
                <MediaInput loading noControl manageRef={mediaInputManageRef} width={sectionStyle?.width ?? 190} height={sectionStyle?.height ?? 180}
                    maxFileSize={20000000} accepts={sectionMimeType} style={{ alignSelf: "center", ...sectionStyle, }}
                    onValidationFail={(_: File, error: string) => {
                        dialogErrorRef.current!.innerText = error;
                        return true;
                    }}
                    onGenerateWithAi={async () => {
                        return new File(["We are here"], "we.txt", { type: 'text/plain' });
                    }} />
                {type === "GENERATE_USING_AI" && (<StatefulLink style={{ marginTop: 20 }} label={labels.upload.dialog.generate_again} onClick={async (done) => {
                    dialogErrorRef.current!.innerText = " ";
                    await mediaInputManageRef.current?.generateWithAi();
                    done();
                }} />)}
                {type === "UPLOAD_FROM_DEVICE" && (<StatefulLink style={{ marginTop: 20 }} label={labels.upload.dialog.select_another} onClick={(done) => {
                    dialogErrorRef.current!.innerText = " ";
                    mediaInputManageRef.current?.select();
                    done();
                }} />)}
                <div ref={dialogErrorRef} className="error" style={{ alignSelf: "flex-start" }}> </div>
            </div>),
            onConfirm: (button?: ButtonManageRef) => {
                dialogErrorRef.current!.innerText = " ";
                const files = mediaInputManageRef.current?.files() as File[];
                if (!files.length) {
                    dialogErrorRef.current!.innerText = labels.upload.dialog.no_media_selected_or_generated;
                    return false;
                }
                const file = files[0];
                button?.setLoadingState(true);
                consusService.uploadFile(`/omnehost/member/media/${section.key}`, `${profile.janus_user_external_id}_${mediaSectionLength}${ObjectHelper.getExtension(file.name, { lastOnly: true, })}`, file).then(({ sanitized }: SanitizedResponse<IUploadedFile>) => {
                    if (!(section.key in profileWebsite?.media)) {
                        (profileWebsite?.media as any)[section.key] = [];
                    }
                    (((profileWebsite?.media as any)?.[section.key] ?? []) as string[]).push(sanitized.consus_download_url);
                    dialog.hide();
                    updateProfileWebsite({ ...profileWebsite, media: (profileWebsite?.media ?? []) });
                }).catch(BaseService.reportError).finally(() => {
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show(() => {
            if (type === "UPLOAD_FROM_DEVICE") {
                mediaInputManageRef.current?.select();
            } else if (type === "GENERATE_USING_AI") {
                mediaInputManageRef.current?.generateWithAi();
            }
        });
    }

    function deleteMedia(sectionKey: string, url: string, isActive: boolean, done: () => void) {
        if (!profileWebsite) return;

        const removeMediaLink = () => {
            const mediaUrls = (((profileWebsite?.media as any)?.[sectionKey] ?? []) as string[]);
            (((profileWebsite?.media as any)?.[sectionKey] ?? []) as string[])?.splice(mediaUrls.indexOf(url), 1);
            updateProfileWebsite({ ...profileWebsite, media: (profileWebsite?.media ?? []) });
            if (isActive) {
                setDefaultMedia(sectionKey, 0);
            }
        };
        consusService.deleteFile([url]).then(removeMediaLink).catch((error: any) => {
            if (error.response.status === 404) {
                removeMediaLink();
                return;
            }
            BaseService.reportError(error);
        }).finally(done);
    }

    async function setDefaultMedia(sectionKey: string, active: number) {
        if (!profileWebsite) return;
        if (!profileWebsite.extra_data) profileWebsite.extra_data = {};
        if (!profileWebsite.extra_data.media) profileWebsite.extra_data.media = {};
        profileWebsite.extra_data.media[sectionKey] = { ...profileWebsite.extra_data.media[sectionKey], active };
        updateProfileWebsite(profileWebsite);
    }

}

export default Media;
